/**
 * Centralized registry for all TanStack Query keys.
 * This ensures consistency and prevents typos.
 *
 * Structure:
 * export const queryKeys = {
 *   featureName: {
 *     all: ['featureName'],
 *     lists: () => [...queryKeys.featureName.all, 'list'],
 *     list: (filters) => [...queryKeys.featureName.lists(), filters],
 *     details: () => [...queryKeys.featureName.all, 'detail'],
 *     detail: (id) => [...queryKeys.featureName.details(), id],
 *   },
 * };
 */

/**
 * Query key type - array of strings or primitives used by TanStack Query
 */
export type QueryKey = readonly (string | number)[];

/**
 * Factory function that takes optional parameters and returns a QueryKey
 */
export type QueryKeyFactory<T = unknown> = (params?: T) => QueryKey;

/**
 * Factory function that takes required parameters and returns a QueryKey
 */
export type QueryKeyFactoryRequired<T> = (params: T) => QueryKey;

// Auth
const authKeys = {
  session: ["auth", "session"] as const,
} as const;

// Users
const usersKeys = {
  all: ["users"] as const,
  me: ["users", "me"] as const,
  detail: (id: number | string) => [...usersKeys.all, "detail", id] as const,
  settings: () => [...usersKeys.all, "settings"] as const,
} as const;

// Permissions
const permissionsKeys = {
  all: ["permissions"] as const,
  current: ["permissions", "current"] as const,
  byUser: (userId: number | string) => ["permissions", userId] as const,
} as const;

// Collections (reference data with long stale time)
const collectionsKeys = {
  all: ["collections"] as const,
  civilites: () => [...collectionsKeys.all, "civilites"] as const,
  filiales: () => [...collectionsKeys.all, "filiales"] as const,
  typeContrats: () => [...collectionsKeys.all, "typeContrats"] as const,
  typeStatuts: () => [...collectionsKeys.all, "typeStatuts"] as const,
  typeRemunerations: () => [...collectionsKeys.all, "typeRemunerations"] as const,
  typeTempsDeTravail: () => [...collectionsKeys.all, "typeTempsDeTravail"] as const,
  etatContrats: () => [...collectionsKeys.all, "etatContrats"] as const,
  categorieDocuments: () => [...collectionsKeys.all, "categorieDocuments"] as const,
  typeDocuments: (categorieCode?: string) =>
    [...collectionsKeys.all, "typeDocuments", categorieCode || "all"] as const,
  competences: (filters?: Record<string, unknown>) =>
    [...collectionsKeys.all, "competences", filters || {}] as const,
  simpleCompetences: (filters?: Record<string, unknown>) =>
    [...collectionsKeys.all, "simpleCompetences", filters || {}] as const,
  typeDemandes: () => [...collectionsKeys.all, "typeDemandes"] as const,
  etatsDemande: () => [...collectionsKeys.all, "etatsDemande"] as const,
  etatDemandeByCode: (code: string) => [...collectionsKeys.all, "etatDemande", code] as const,
  typePlanningItems: () => [...collectionsKeys.all, "typePlanningItems"] as const,
  planningTemplates: () => [...collectionsKeys.all, "planningTemplates"] as const,
  holidays: () => [...collectionsKeys.all, "holidays"] as const,
  typeFournisseurs: () => [...collectionsKeys.all, "typeFournisseurs"] as const,
  departments: (filialeCodes?: string[]) =>
    [...collectionsKeys.all, "departments", filialeCodes?.join(",") || "all"] as const,
} as const;

// API Resource query keys (used by useTable hook)
// Format: [resource, "list", {...params}]
const apiResources = {
  personnes: {
    lists: () => ["/personnes", "list"] as const,
    list: (params?: Record<string, unknown>) =>
      [...apiResources.personnes.lists(), params || {}] as const,
  },
} as const;

// RH module query keys
const rhKeys = {
  all: ["rh"] as const,
  dashboard: () => [...rhKeys.all, "dashboard"] as const,
  metrics: () => [...rhKeys.dashboard(), "metrics"] as const,
  personnes: {
    all: () => [...rhKeys.all, "personnes"] as const,
    search: (query?: string) => [...rhKeys.personnes.all(), "search", query || ""] as const,
    managers: () => [...rhKeys.personnes.all(), "managers"] as const,
  },
  collaborateurs: {
    all: () => [...rhKeys.all, "collaborateurs"] as const,
    metrics: () => [...rhKeys.collaborateurs.all(), "metrics"] as const,
    details: () => [...rhKeys.collaborateurs.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.collaborateurs.details(), id] as const,
  },
  competences: {
    all: () => [...rhKeys.all, "competences"] as const,
    lists: () => [...rhKeys.competences.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...rhKeys.competences.lists(), filters || {}] as const,
    details: () => [...rhKeys.competences.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.competences.details(), id] as const,
    metrics: () => [...rhKeys.competences.all(), "metrics"] as const,
  },
  contrats: {
    all: () => [...rhKeys.all, "contrats"] as const,
    metrics: (expirationMonths?: number) =>
      [...rhKeys.contrats.all(), "metrics", expirationMonths ?? 1] as const,
    details: () => [...rhKeys.contrats.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.contrats.details(), id] as const,
    lists: () => [...rhKeys.contrats.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...rhKeys.contrats.lists(), filters] as const,
    byEtat: (etatCode: string) => [...rhKeys.contrats.all(), "byEtat", etatCode] as const,
    signature: (token: string) => [...rhKeys.contrats.all(), "signature", token] as const,
    hoursSummary: (id: number | string) => [...rhKeys.contrats.all(), "hours-summary", id] as const,
    avenants: (contratId: number | string) =>
      [...rhKeys.contrats.all(), "avenants", contratId] as const,
  },
  avenants: {
    all: () => [...rhKeys.all, "avenants"] as const,
    lists: () => [...rhKeys.avenants.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...rhKeys.avenants.lists(), filters] as const,
    details: () => [...rhKeys.avenants.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.avenants.details(), id] as const,
    byContrat: (contratId: number | string) =>
      [...rhKeys.avenants.all(), "byContrat", contratId] as const,
    pendingMe: () => [...rhKeys.avenants.all(), "pendingMe"] as const,
    signedMe: () => [...rhKeys.avenants.all(), "signedMe"] as const,
  },
  planningTemplate: {
    all: () => [...rhKeys.all, "planning-template"] as const,
    detail: (id: number | string) => [...rhKeys.planningTemplate.all(), "detail", id] as const,
  },
  horairesDeTravail: {
    all: () => [...rhKeys.all, "horaires-de-travail"] as const,
    lists: () => [...rhKeys.horairesDeTravail.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...rhKeys.horairesDeTravail.lists(), filters || {}] as const,
    metrics: () => [...rhKeys.horairesDeTravail.all(), "metrics"] as const,
    details: () => [...rhKeys.horairesDeTravail.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.horairesDeTravail.details(), id] as const,
    personnes: (planningTemplateId: number | string) =>
      [...rhKeys.horairesDeTravail.detail(planningTemplateId), "personnes"] as const,
  },
  compteursTemps: {
    all: () => [...rhKeys.all, "compteurs-temps"] as const,
    detail: (personneId: number | string) =>
      [...rhKeys.compteursTemps.all(), "detail", personneId] as const,
  },
  compteursSolde: {
    all: () => [...rhKeys.all, "compteurs-solde"] as const,
    detail: (personneId: number | string) =>
      [...rhKeys.compteursSolde.all(), "detail", personneId] as const,
  },
  correctionsSolde: {
    all: () => [...rhKeys.all, "corrections-solde"] as const,
    detail: (personneId: number | string) =>
      [...rhKeys.correctionsSolde.all(), "detail", personneId] as const,
  },
  absences: {
    all: () => [...rhKeys.all, "absences"] as const,
    metrics: () => [...rhKeys.absences.all(), "metrics"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...rhKeys.absences.all(), "list", filters] as const,
    detail: (id: number | string) => [...rhKeys.absences.all(), "detail", id] as const,
    calendar: (filters?: Record<string, unknown>) =>
      [...rhKeys.absences.all(), "calendar", filters] as const,
    validationState: (id: number | string) =>
      [...rhKeys.absences.detail(id), "validation-state"] as const,
  },
  documents: {
    all: () => [...rhKeys.all, "documents"] as const,
    lists: () => [...rhKeys.documents.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...rhKeys.documents.lists(), filters || {}] as const,
    details: () => [...rhKeys.documents.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.documents.details(), id] as const,
    download: (id: number | string) => [...rhKeys.documents.all(), "download", id] as const,
    byPersonne: (personneId: number | string) =>
      [...rhKeys.documents.all(), "byPersonne", personneId] as const,
    byContrat: (contratId: number | string) =>
      [...rhKeys.documents.all(), "byContrat", contratId] as const,
    export: () => [...rhKeys.documents.all(), "export"] as const,
  },
  feuillesDeTemps: {
    // Base key matches useTable resource name ("/feuille_de_temps") for invalidation alignment
    all: () => ["/feuille_de_temps"] as const,
    metrics: () => [...rhKeys.feuillesDeTemps.all(), "metrics"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...rhKeys.feuillesDeTemps.all(), "list", filters] as const,
    detail: (id: number | string) => [...rhKeys.feuillesDeTemps.all(), "detail", id] as const,
    weekSummary: (personneId: number, weekNumber: number, year: number) =>
      [...rhKeys.feuillesDeTemps.all(), "week-summary", { personneId, weekNumber, year }] as const,
  },
  notesDeFrais: {
    all: () => [...rhKeys.all, "notes-de-frais"] as const,
    metrics: () => [...rhKeys.notesDeFrais.all(), "metrics"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...rhKeys.notesDeFrais.all(), "list", filters] as const,
    detail: (id: number | string) => [...rhKeys.notesDeFrais.all(), "detail", id] as const,
  },
  planification: {
    all: () => [...rhKeys.all, "planification"] as const,
    items: (filters?: Record<string, unknown>) =>
      [...rhKeys.planification.all(), "items", filters || {}] as const,
    item: (id: number | string) => [...rhKeys.planification.all(), "item", id] as const,
  },
  pointage: {
    all: () => [...rhKeys.all, "pointage"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...rhKeys.pointage.all(), "list", filters || {}] as const,
    item: (id: number | string) => [...rhKeys.pointage.all(), "item", id] as const,
    context: (date?: string) => [...rhKeys.pointage.all(), "me", "context", date ?? ""] as const,
    kiosk: (matricule?: string) => [...rhKeys.pointage.all(), "kiosk", matricule ?? ""] as const,
    site: (hash?: string) => [...rhKeys.pointage.all(), "site", hash ?? ""] as const,
  },
} as const;

// Pointage module query keys
const pointageKeys = {
  all: ["pointage"] as const,
  checkin: () => [...pointageKeys.all, "checkin"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...pointageKeys.all, "list", filters || {}] as const,
  item: (id: number | string) => [...pointageKeys.all, "item", id] as const,
} as const;

// Planning module query keys
const planningKeys = {
  all: ["planning"] as const,
  items: (filters?: Record<string, unknown>) =>
    [...planningKeys.all, "items", filters || {}] as const,
  item: (id: number | string) => [...planningKeys.all, "item", id] as const,
  events: (personneId?: number | null, startDate?: string | null, endDate?: string | null) =>
    ["planning-events", personneId ?? null, startDate ?? null, endDate ?? null] as const,
} as const;

// Creation module query keys
const creationKeys = {
  all: ["creation"] as const,
  articles: {
    all: () => [...creationKeys.all, "articles"] as const,
    metrics: () => [...creationKeys.articles.all(), "metrics"] as const,
    lists: () => [...creationKeys.articles.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...creationKeys.articles.lists(), filters] as const,
    details: () => [...creationKeys.articles.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.articles.details(), id] as const,
    export: () => [...creationKeys.articles.all(), "export"] as const,
  },
  documents: {
    all: () => [...creationKeys.all, "documents"] as const,
    lists: () => [...creationKeys.documents.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...creationKeys.documents.lists(), filters || {}] as const,
    details: () => [...creationKeys.documents.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.documents.details(), id] as const,
    download: (id: number | string) => [...creationKeys.documents.all(), "download", id] as const,
  },
  ateliers: {
    all: () => [...creationKeys.all, "ateliers"] as const,
    metrics: () => [...creationKeys.ateliers.all(), "metrics"] as const,
    lists: () => [...creationKeys.ateliers.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...creationKeys.ateliers.lists(), filters] as const,
    details: () => [...creationKeys.ateliers.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.ateliers.details(), id] as const,
  },
  postes: {
    all: () => [...creationKeys.all, "postes"] as const,
    metrics: () => [...creationKeys.postes.all(), "metrics"] as const,
    lists: () => [...creationKeys.postes.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.postes.lists(), filters] as const,
    details: () => [...creationKeys.postes.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.postes.details(), id] as const,
    export: () => [...creationKeys.postes.all(), "export"] as const,
  },
  prestations: {
    all: () => [...creationKeys.all, "prestations"] as const,
    metrics: () => [...creationKeys.prestations.all(), "metrics"] as const,
    lists: () => [...creationKeys.prestations.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...creationKeys.prestations.lists(), filters] as const,
    details: () => [...creationKeys.prestations.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.prestations.details(), id] as const,
  },
  dossierTechniques: {
    all: () => [...creationKeys.all, "dossierTechniques"] as const,
    metrics: () => [...creationKeys.dossierTechniques.all(), "metrics"] as const,
    lists: () => [...creationKeys.dossierTechniques.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...creationKeys.dossierTechniques.lists(), filters] as const,
    details: () => [...creationKeys.dossierTechniques.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.dossierTechniques.details(), id] as const,
  },
  ldfs: {
    all: () => [...creationKeys.all, "ldfs"] as const,
    metrics: () => [...creationKeys.ldfs.all(), "metrics"] as const,
    lists: () => [...creationKeys.ldfs.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.ldfs.lists(), filters] as const,
    details: () => [...creationKeys.ldfs.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.ldfs.details(), id] as const,
  },
  dashboard: () => [...creationKeys.all, "dashboard"] as const,
} as const;

// Stock module query keys
const stockKeys = {
  all: ["stock"] as const,
  dashboard: () => [...stockKeys.all, "dashboard"] as const,
  summary: {
    all: () => [...stockKeys.all, "summary"] as const,
    dashboard: () => [...stockKeys.summary.all(), "dashboard"] as const,
  },
  depots: {
    all: () => [...stockKeys.all, "depots"] as const,
    lists: () => [...stockKeys.depots.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => {
      const entries: string[] = [];
      if (filters) {
        Object.keys(filters)
          .sort()
          .forEach((key) => {
            const value = filters[key];
            if (value !== undefined && value !== "") {
              entries.push(key, String(value));
            }
          });
      }
      return [...stockKeys.depots.lists(), ...entries] as const;
    },
    details: () => [...stockKeys.depots.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.depots.details(), id] as const,
  },
  zones: {
    all: () => [...stockKeys.all, "zones"] as const,
    lists: () => [...stockKeys.zones.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => {
      const entries: string[] = [];
      if (filters) {
        Object.keys(filters)
          .sort()
          .forEach((key) => {
            const value = filters[key];
            if (value !== undefined && value !== "") {
              entries.push(key, String(value));
            }
          });
      }
      return [...stockKeys.zones.lists(), ...entries] as const;
    },
    details: () => [...stockKeys.zones.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.zones.details(), id] as const,
  },
  emplacements: {
    all: () => [...stockKeys.all, "emplacements"] as const,
    lists: () => [...stockKeys.emplacements.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => {
      const entries: string[] = [];
      if (filters) {
        Object.keys(filters)
          .sort()
          .forEach((key) => {
            const value = filters[key];
            if (value !== undefined && value !== "") {
              entries.push(key, String(value));
            }
          });
      }
      return [...stockKeys.emplacements.lists(), ...entries] as const;
    },
    details: () => [...stockKeys.emplacements.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.emplacements.details(), id] as const,
  },
  movements: {
    all: () => [...stockKeys.all, "movements"] as const,
    lists: () => [...stockKeys.movements.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => {
      const entries: string[] = [];
      if (filters) {
        Object.keys(filters)
          .sort()
          .forEach((key) => {
            const value = filters[key];
            if (value !== undefined && value !== "") {
              entries.push(key, String(value));
            }
          });
      }
      return [...stockKeys.movements.lists(), ...entries] as const;
    },
    details: () => [...stockKeys.movements.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.movements.details(), id] as const,
  },
  inventaires: {
    all: () => [...stockKeys.all, "inventaires"] as const,
    lists: () => [...stockKeys.inventaires.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => {
      const entries: string[] = [];
      if (filters) {
        Object.keys(filters)
          .sort()
          .forEach((key) => {
            const value = filters[key];
            if (value !== undefined && value !== "") {
              entries.push(key, String(value));
            }
          });
      }
      return [...stockKeys.inventaires.lists(), ...entries] as const;
    },
    details: () => [...stockKeys.inventaires.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.inventaires.details(), id] as const,
    missions: {
      all: () => [...stockKeys.inventaires.all(), "missions"] as const,
      lists: () => [...stockKeys.inventaires.missions.all(), "list"] as const,
      list: (inventaireId?: number | string) =>
        [...stockKeys.inventaires.missions.lists(), inventaireId] as const,
    },
  },
  mouvementsMetrics: {
    all: () => [...stockKeys.all, "mouvementsMetrics"] as const,
  },
  inventairesMetrics: {
    all: () => [...stockKeys.all, "inventairesMetrics"] as const,
  },
  consultation: {
    all: () => [...stockKeys.all, "consultation"] as const,
    stats: (filters?: Record<string, unknown>) => {
      const entries: string[] = ["stats"];
      if (filters) {
        Object.keys(filters)
          .sort()
          .forEach((key) => {
            const value = filters[key];
            if (value !== undefined && value !== "") {
              entries.push(key, String(value));
            }
          });
      }
      return [...stockKeys.consultation.all(), ...entries] as const;
    },
    details: {
      all: () => [...stockKeys.consultation.all(), "details"] as const,
      list: (filters?: Record<string, unknown>) => {
        const entries: string[] = ["list"];
        if (filters) {
          Object.keys(filters)
            .sort()
            .forEach((key) => {
              const value = filters[key];
              if (value !== undefined && value !== "") {
                entries.push(key, String(value));
              }
            });
        }
        return [...stockKeys.consultation.details.all(), ...entries] as const;
      },
    },
  },
} as const;

// Achats module query keys
const achatsKeys = {
  all: ["achats"] as const,
  dashboard: () => [...achatsKeys.all, "dashboard"] as const,
  fournisseurs: {
    all: () => [...achatsKeys.all, "fournisseurs"] as const,
    lists: () => [...achatsKeys.fournisseurs.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...achatsKeys.fournisseurs.lists(), filters || {}] as const,
    details: () => [...achatsKeys.fournisseurs.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.fournisseurs.details(), id] as const,
    metrics: () => [...achatsKeys.fournisseurs.all(), "metrics"] as const,
    export: () => [...achatsKeys.fournisseurs.all(), "export"] as const,
  },
  fournitures: {
    all: () => [...achatsKeys.all, "fournitures"] as const,
    lists: () => [...achatsKeys.fournitures.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...achatsKeys.fournitures.lists(), filters || {}] as const,
    details: () => [...achatsKeys.fournitures.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.fournitures.details(), id] as const,
    metrics: () => [...achatsKeys.fournitures.all(), "metrics"] as const,
  },
  prestations: {
    all: () => [...achatsKeys.all, "prestations"] as const,
    lists: () => [...achatsKeys.prestations.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...achatsKeys.prestations.lists(), filters || {}] as const,
    details: () => [...achatsKeys.prestations.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.prestations.details(), id] as const,
    metrics: () => [...achatsKeys.prestations.all(), "metrics"] as const,
  },
  famillesPrestation: {
    all: () => [...achatsKeys.all, "famillesPrestation"] as const,
    lists: () => [...achatsKeys.famillesPrestation.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...achatsKeys.famillesPrestation.lists(), filters || {}] as const,
  },
  commandes: {
    all: () => [...achatsKeys.all, "commandes"] as const,
    lists: () => [...achatsKeys.commandes.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...achatsKeys.commandes.lists(), filters || {}] as const,
    details: () => [...achatsKeys.commandes.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.commandes.details(), id] as const,
    metrics: () => [...achatsKeys.commandes.all(), "metrics"] as const,
  },
  receptions: {
    all: () => [...achatsKeys.all, "receptions"] as const,
    lists: () => [...achatsKeys.receptions.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...achatsKeys.receptions.lists(), filters || {}] as const,
    details: () => [...achatsKeys.receptions.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.receptions.details(), id] as const,
    metrics: () => [...achatsKeys.receptions.all(), "metrics"] as const,
  },
  factures: {
    all: () => [...achatsKeys.all, "factures"] as const,
    lists: () => [...achatsKeys.factures.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...achatsKeys.factures.lists(), filters || {}] as const,
    details: () => [...achatsKeys.factures.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.factures.details(), id] as const,
    metrics: () => [...achatsKeys.factures.all(), "metrics"] as const,
    brDisponibles: (id: number | string) =>
      [...achatsKeys.factures.all(), "br-disponibles", id] as const,
  },
} as const;

// Ventes module query keys
const ventesKeys = {
  all: ["ventes"] as const,
  dashboard: () => [...ventesKeys.all, "dashboard"] as const,
  categories: {
    all: () => [...ventesKeys.all, "categories"] as const,
    lists: () => [...ventesKeys.categories.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.categories.lists(), filters || {}] as const,
    details: () => [...ventesKeys.categories.all(), "detail"] as const,
    detail: (id: number | string) => [...ventesKeys.categories.details(), id] as const,
    metrics: () => [...ventesKeys.categories.all(), "metrics"] as const,
  },
  clients: {
    all: () => [...ventesKeys.all, "clients"] as const,
    lists: () => [...ventesKeys.clients.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.clients.lists(), filters || {}] as const,
    details: () => [...ventesKeys.clients.all(), "detail"] as const,
    detail: (id: number | string) => [...ventesKeys.clients.details(), id] as const,
    metrics: () => [...ventesKeys.clients.all(), "metrics"] as const,
  },
  commandes: {
    all: () => [...ventesKeys.all, "commandes"] as const,
    lists: () => [...ventesKeys.commandes.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.commandes.lists(), filters || {}] as const,
    details: () => [...ventesKeys.commandes.all(), "detail"] as const,
    detail: (id: number | string) => [...ventesKeys.commandes.details(), id] as const,
    metrics: () => [...ventesKeys.commandes.all(), "metrics"] as const,
  },
  expeditions: {
    all: () => [...ventesKeys.all, "expeditions"] as const,
    lists: () => [...ventesKeys.expeditions.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.expeditions.lists(), filters || {}] as const,
    details: () => [...ventesKeys.expeditions.all(), "detail"] as const,
    detail: (id: string | number) => [...ventesKeys.expeditions.details(), id] as const,
    metrics: () => [...ventesKeys.expeditions.all(), "metrics"] as const,
  },
  articles: {
    all: () => [...ventesKeys.all, "articles"] as const,
    lists: () => [...ventesKeys.articles.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.articles.lists(), filters || {}] as const,
  },
  transporteurs: {
    all: () => [...ventesKeys.all, "transporteurs"] as const,
    lists: () => [...ventesKeys.transporteurs.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.transporteurs.lists(), filters || {}] as const,
  },
  preparateurs: {
    all: () => [...ventesKeys.all, "preparateurs"] as const,
    lists: () => [...ventesKeys.preparateurs.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.preparateurs.lists(), filters || {}] as const,
  },
  factures: {
    all: () => [ventesKeys.all[0], "factures"] as const,
    lists: () => [...ventesKeys.factures.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...ventesKeys.factures.lists(), filters || {}] as const,
    details: () => [...ventesKeys.factures.all(), "detail"] as const,
    detail: (id: string | number) => [...ventesKeys.factures.details(), id] as const,
    metrics: () => [...ventesKeys.factures.all(), "metrics"] as const,
    transferts: () => [...ventesKeys.factures.all(), "transferts"] as const,
    beDisponibles: (id: string | number) =>
      [...ventesKeys.factures.all(), "be-disponibles", id] as const,
  },
  tarifs: {
    all: () => [...ventesKeys.all, "tarifs"] as const,
    stats: () => [...ventesKeys.tarifs.all(), "stats"] as const,
    grilles: {
      all: () => [...ventesKeys.tarifs.all(), "grilles"] as const,
      lists: () => [...ventesKeys.tarifs.grilles.all(), "list"] as const,
      list: (filters?: Record<string, unknown>) =>
        [...ventesKeys.tarifs.grilles.lists(), filters || {}] as const,
      details: () => [...ventesKeys.tarifs.grilles.all(), "detail"] as const,
      detail: (id: number | string) => [...ventesKeys.tarifs.grilles.details(), id] as const,
    },
    promotions: {
      all: () => [...ventesKeys.tarifs.all(), "promotions"] as const,
      lists: () => [...ventesKeys.tarifs.promotions.all(), "list"] as const,
      list: (filters?: Record<string, unknown>) =>
        [...ventesKeys.tarifs.promotions.lists(), filters || {}] as const,
      details: () => [...ventesKeys.tarifs.promotions.all(), "detail"] as const,
      detail: (id: number | string) => [...ventesKeys.tarifs.promotions.details(), id] as const,
    },
    profils: {
      all: () => [...ventesKeys.tarifs.all(), "profils"] as const,
      lists: () => [...ventesKeys.tarifs.profils.all(), "list"] as const,
      list: (filters?: Record<string, unknown>) =>
        [...ventesKeys.tarifs.profils.lists(), filters || {}] as const,
      details: () => [...ventesKeys.tarifs.profils.all(), "detail"] as const,
      detail: (id: number | string) => [...ventesKeys.tarifs.profils.details(), id] as const,
    },
    taxes: {
      all: () => [...ventesKeys.tarifs.all(), "taxes"] as const,
      lists: () => [...ventesKeys.tarifs.taxes.all(), "list"] as const,
      list: (filters?: Record<string, unknown>) =>
        [...ventesKeys.tarifs.taxes.lists(), filters || {}] as const,
      details: () => [...ventesKeys.tarifs.taxes.all(), "detail"] as const,
      detail: (id: number | string) => [...ventesKeys.tarifs.taxes.details(), id] as const,
    },
    organismes: {
      all: () => [...ventesKeys.tarifs.all(), "organismes"] as const,
      lists: () => [...ventesKeys.tarifs.organismes.all(), "list"] as const,
      list: (filters?: Record<string, unknown>) =>
        [...ventesKeys.tarifs.organismes.lists(), filters || {}] as const,
      details: () => [...ventesKeys.tarifs.organismes.all(), "detail"] as const,
      detail: (id: number | string) => [...ventesKeys.tarifs.organismes.details(), id] as const,
    },
  },
} as const;

// Production module query keys
const productionKeys = {
  all: ["production"] as const,
  dashboard: () => [...productionKeys.all, "dashboard"] as const,
  ordresFabrication: {
    all: () => [...productionKeys.all, "ordresFabrication"] as const,
    lists: () => [...productionKeys.ordresFabrication.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...productionKeys.ordresFabrication.lists(), filters || {}] as const,
    details: () => [...productionKeys.ordresFabrication.all(), "detail"] as const,
    detail: (id: number | string) => [...productionKeys.ordresFabrication.details(), id] as const,
  },
  calculBesoins: {
    all: () => [...productionKeys.all, "calculBesoins"] as const,
    lists: () => [...productionKeys.calculBesoins.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...productionKeys.calculBesoins.lists(), filters || {}] as const,
    details: () => [...productionKeys.calculBesoins.all(), "detail"] as const,
    detail: (id: number | string) => [...productionKeys.calculBesoins.details(), id] as const,
  },
  lancements: {
    all: () => [...productionKeys.all, "lancements"] as const,
    lists: () => [...productionKeys.lancements.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...productionKeys.lancements.lists(), filters || {}] as const,
    details: () => [...productionKeys.lancements.all(), "detail"] as const,
    detail: (id: number | string) => [...productionKeys.lancements.details(), id] as const,
  },
  declarations: {
    all: () => [...productionKeys.all, "declarations"] as const,
    lists: () => [...productionKeys.declarations.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...productionKeys.declarations.lists(), filters || {}] as const,
    details: () => [...productionKeys.declarations.all(), "detail"] as const,
    detail: (id: number | string) => [...productionKeys.declarations.details(), id] as const,
  },
  cloture: {
    all: () => [...productionKeys.all, "cloture"] as const,
    lists: () => [...productionKeys.cloture.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...productionKeys.cloture.lists(), filters || {}] as const,
    details: () => [...productionKeys.cloture.all(), "detail"] as const,
    detail: (id: number | string) => [...productionKeys.cloture.details(), id] as const,
  },
} as const;

// Security module query keys
const securityKeys = {
  all: ["security"] as const,
  users: {
    all: () => [...securityKeys.all, "users"] as const,
    lists: () => [...securityKeys.users.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...securityKeys.users.lists(), filters || {}] as const,
    details: () => [...securityKeys.users.all(), "detail"] as const,
    detail: (id: number | string) => [...securityKeys.users.details(), id] as const,
    permissions: (id: number | string) =>
      [...securityKeys.users.detail(id), "permissions"] as const,
    directPermissions: (id: number | string) =>
      [...securityKeys.users.detail(id), "direct-permissions"] as const,
  },
  profiles: {
    all: () => [...securityKeys.all, "profiles"] as const,
    lists: () => [...securityKeys.profiles.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...securityKeys.profiles.lists(), filters || {}] as const,
    details: () => [...securityKeys.profiles.all(), "detail"] as const,
    detail: (id: number | string) => [...securityKeys.profiles.details(), id] as const,
    permissions: (id: number | string) =>
      [...securityKeys.profiles.detail(id), "permissions"] as const,
  },
  permissions: {
    all: () => [...securityKeys.all, "permissions"] as const,
    list: () => [...securityKeys.permissions.all(), "list"] as const,
  },
} as const;

// Notifications query keys
const notificationsKeys = {
  // Real-time notifications (used by Mercure - gets invalidated on new/delete)
  all: ["notifications"] as const,
  lists: () => [...notificationsKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) => [...notificationsKeys.lists(), filters] as const,
  unreadCount: () => [...notificationsKeys.all, "unread-count"] as const,
} as const;

// Comptabilité
const comptabiliteKeys = {
  all: ["comptabilite"] as const,
  relances: {
    all: () => [...comptabiliteKeys.all, "relances"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...comptabiliteKeys.relances.all(), "list", filters] as const,
    detail: (id: number | string) => [...comptabiliteKeys.relances.all(), "detail", id] as const,
  },
  ecotaxes: {
    all: () => [...comptabiliteKeys.all, "ecotaxes"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...comptabiliteKeys.ecotaxes.all(), "list", filters] as const,
    detail: (id: number | string) => [...comptabiliteKeys.ecotaxes.all(), "detail", id] as const,
  },
  echeances: {
    all: () => [...comptabiliteKeys.all, "echeances"] as const,
    list: () => [...comptabiliteKeys.echeances.all(), "list"] as const,
  },
  reglements: {
    all: () => [...comptabiliteKeys.all, "reglements"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...comptabiliteKeys.reglements.all(), "list", filters || {}] as const,
    detail: (id: number | string) => [...comptabiliteKeys.reglements.all(), "detail", id] as const,
  },
  dashboard: () => [...comptabiliteKeys.all, "dashboard"] as const,
} as const;

// Tickets
const ticketsKeys = {
  all: ["tickets"] as const,
  list: (filters?: Record<string, unknown>) => [...ticketsKeys.all, "list", filters || {}] as const,
  detail: (id: number | string) => [...ticketsKeys.all, "detail", id] as const,
  metrics: () => [...ticketsKeys.all, "metrics"] as const,
  comments: {
    all: (ticketId: number | string) => [...ticketsKeys.all, "comments", ticketId] as const,
  },
  attachments: {
    all: (ticketId: number | string) => [...ticketsKeys.all, "attachments", ticketId] as const,
  },
} as const;

/**
 * Dashboard query keys for conges and other dashboard-related queries
 */
const dashboardKeys = {
  all: ["dashboard"] as const,
  conges: {
    all: () => [...dashboardKeys.all, "conges"] as const,
    summary: () => [...dashboardKeys.conges.all(), "summary"] as const,
  },
} as const;

/**
 * Main query keys registry - exported for backward compatibility
 */
export const queryKeys = {
  auth: authKeys,
  users: usersKeys,
  permissions: permissionsKeys,
  collections: collectionsKeys,
  rh: rhKeys,
  pointage: pointageKeys,
  planning: planningKeys,
  creation: creationKeys,
  production: productionKeys,
  stock: stockKeys,
  achats: achatsKeys,
  ventes: ventesKeys,
  comptabilite: comptabiliteKeys,
  tickets: ticketsKeys,
  notifications: notificationsKeys,
  security: securityKeys,
  apiResources,
} as const;

/**
 * Dashboard query keys - exported separately for modular access
 */
export const dashboardQueryKeys = dashboardKeys;

export type QueryKeys = typeof queryKeys;
export type DashboardQueryKeys = typeof dashboardQueryKeys;
